import { Button, Skeleton } from '@longy/web-components'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Check, LoaderCircle, TriangleAlert } from 'lucide-react'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { match } from 'ts-pattern'
import { z } from 'zod'

import { BookingStep } from '../../components/booking/BookingStep'
import { api } from '../../core/trpc'
import { resolveBookingConfirmationView } from '../../utils/bookingConfirmation'

const PATH = '/booking/success'

/**
 * The webhook that makes the booking usually lands within a second or two of the redirect. The
 * app-wide default is a 60-second refetch, which would leave somebody staring at "booking not yet
 * made" for most of a minute over a booking that was made while they read it.
 */
const CONFIRMATION_POLL_MS = 2000

// Long enough to cover a slow webhook, short enough that a real failure is reported rather than
// spun on forever.
const CONFIRMATION_TIMEOUT_MS = 45000

const successSearchSchema = z.object({
    transactionId: z.string().optional(),
})

const BookingSuccessPage = () => {
    const { t } = useTranslation()
    const { transactionId } = Route.useSearch()

    const landedAt = useRef(Date.now())
    const elapsedMs = Date.now() - landedAt.current

    const { data: transaction, isLoading } = api.webCheckout.getTransaction.useQuery(
        { transactionId: transactionId ?? '' },
        {
            enabled: !!transactionId,
            /**
             * Re-evaluated after every poll, so the deadline is read against the clock rather than
             * a render — the first evaluation past it returns false and the polling stops on its
             * own, with nothing left running behind an abandoned tab.
             */
            refetchInterval: (query) =>
                !query.state.data?.appointmentId &&
                Date.now() - landedAt.current < CONFIRMATION_TIMEOUT_MS
                    ? CONFIRMATION_POLL_MS
                    : false,
        },
    )

    /**
     * The charge settling is not the booking being made: everything that hangs off it — the
     * purchase, the appointment, the reminders — happens afterwards in
     * `processWebPaymentCompletion`. Announcing a confirmed booking on the strength of the payment
     * alone told people their appointment existed when it did not.
     */
    const view = resolveBookingConfirmationView({
        isLoading,
        hasAppointment: !!transaction?.appointmentId,
        elapsedMs,
        timeoutMs: CONFIRMATION_TIMEOUT_MS,
    })

    if (view === 'loading') {
        return (
            <BookingStep
                title={t('frontend.booking.bookingConfirmed')}
                step={t('frontend.booking.title')}
            >
                <Skeleton className="h-48 w-full" />
            </BookingStep>
        )
    }

    const title = match(view)
        .with('booked', () => t('frontend.booking.bookingConfirmed'))
        .with('confirming', () => t('frontend.booking.bookingConfirming'))
        .with('incomplete', () => t('frontend.booking.bookingIncomplete'))
        .exhaustive()

    return (
        <BookingStep title={title} step={t('frontend.booking.title')}>
            <div className="flex flex-col items-center gap-6 rounded-lg border border-slate-200 bg-white p-10">
                {match(view)
                    .with('booked', () => <Check className="size-14 text-primary" />)
                    .with('confirming', () => (
                        <LoaderCircle className="size-14 animate-spin text-primary" />
                    ))
                    .with('incomplete', () => <TriangleAlert className="size-14 text-amber-500" />)
                    .exhaustive()}
                <p className="text-center text-brand-gray">
                    {match(view)
                        .with('booked', () => t('frontend.booking.bookingConfirmedBody'))
                        .with('confirming', () => t('frontend.booking.bookingConfirmingBody'))
                        .with('incomplete', () =>
                            t('frontend.booking.bookingIncompleteBody', {
                                transactionId: transactionId ?? '',
                            }),
                        )
                        .exhaustive()}
                </p>
                <Button asChild>
                    <Link to="/bookings">{t('frontend.booking.backToBookings')}</Link>
                </Button>
            </div>
        </BookingStep>
    )
}

export const Route = createFileRoute(PATH)({
    validateSearch: successSearchSchema,
    head: () => ({ meta: [{ title: 'Long-Y - Foglalás visszaigazolva' }] }),
    component: BookingSuccessPage,
})
